import { Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { toast } from 'react-toastify';

import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import { updateUnitPmsConfig } from '@/Units/api';
import type { Unit } from '@/Units/types';

import type { PmsType, ResmanConfig } from '../../types';
import { useBillableUnits } from './pmsUtils';
import {
  buildResmanInitialUnitMappings,
  buildResmanPersistedUnitMappings,
  buildResmanStructureConfig,
  buildResmanUnitConfig,
  validateResmanFormState,
} from './providers/resman';

// ResMan Payment Type dropdown options. The semantic role of this column on a charge
// (vs. payment) row is UNCONFIRMED from public ResMan docs; the values below are taken
// from generic property-management-software conventions and may not be the exact set
// ResMan validates against. Treat as a starting point until a real import confirms.
const RESMAN_PAYMENT_TYPES = ['Check', 'Cash', 'Money Order', 'Credit Card', 'ACH', 'EFT', 'Other'];

const useStyles = makeStyles(() => ({
  editIcon: {
    opacity: 0.3,
    '&:hover': { opacity: 1 },
  },
  fieldLabel: {
    fontWeight: 600,
    marginBottom: '4px',
  },
  fieldValue: { color: '#666' },
  placeholder: { color: '#999', fontStyle: 'italic' },
}));

interface ResmanConfigFormProps {
  pmsConfig: ResmanConfig | null;
  units: Unit[];
  onUpdate: (pmsType: PmsType | null, pmsConfig: ResmanConfig | null) => Promise<void>;
  onRefresh?: () => void;
  /** When provided, the form's "Cancel" button calls this instead of toggling local edit mode.
   * Used by the dialog wrapper in PmsActions to close the dialog on cancel. */
  onCancel?: () => void;
}

export const ResmanConfigForm = ({
  pmsConfig,
  units,
  onUpdate,
  onRefresh,
  onCancel,
}: ResmanConfigFormProps): JSX.Element => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = React.useState(!pmsConfig);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showClearConfirm, setShowClearConfirm] = React.useState(false);
  // Submit-time validation flag. Stays false until the user clicks Save with an
  // incomplete form; then required fields turn red. Cleared as the user fills them in.
  const [showRequiredErrors, setShowRequiredErrors] = React.useState(false);

  const [propertyShortName, setPropertyShortName] = React.useState(
    pmsConfig?.property_short_name ?? '',
  );
  const [defaultTransactionCategory, setDefaultTransactionCategory] = React.useState(
    pmsConfig?.default_transaction_category ?? '',
  );
  const [defaultPaymentType, setDefaultPaymentType] = React.useState(
    pmsConfig?.default_payment_type ?? '',
  );
  const [defaultSplitLedger, setDefaultSplitLedger] = React.useState(
    pmsConfig?.default_split_ledger ?? '',
  );

  const billableUnits = useBillableUnits(units);

  // Persisted-only view of unit mappings (drives change detection on save and the
  // "N of M units mapped" read-only count). Same logic as buildResmanPersistedUnitMappings
  // but memoized for hot paths.
  const persistedMappings = React.useMemo(
    () => buildResmanPersistedUnitMappings(billableUnits),
    [billableUnits],
  );

  // Editable mappings:
  //  - First-time setup (no pmsConfig): pre-fill with SimpleSUB names so the form
  //    is useful at a glance.
  //  - Edit flow on configured property: show only persisted values, so the form
  //    reflects exactly what is saved (a cleared mapping stays cleared).
  const [unitMappings, setUnitMappings] = React.useState<Record<string, string>>(() =>
    pmsConfig
      ? buildResmanPersistedUnitMappings(billableUnits)
      : buildResmanInitialUnitMappings(billableUnits),
  );

  // Reset local state when props change. Mirrors the init choice above.
  React.useEffect(() => {
    setPropertyShortName(pmsConfig?.property_short_name ?? '');
    setDefaultTransactionCategory(pmsConfig?.default_transaction_category ?? '');
    setDefaultPaymentType(pmsConfig?.default_payment_type ?? '');
    setDefaultSplitLedger(pmsConfig?.default_split_ledger ?? '');
    setUnitMappings(
      pmsConfig
        ? buildResmanPersistedUnitMappings(billableUnits)
        : buildResmanInitialUnitMappings(billableUnits),
    );
    setIsEditing(!pmsConfig);
  }, [pmsConfig, billableUnits]);

  const mappedCount = React.useMemo(
    () => billableUnits.filter((u) => u.pms_config?.unit_name).length,
    [billableUnits],
  );

  const handleStartEdit = () => {
    // `unitMappings` is already pre-filled by the init helper, so we just flip the
    // edit flag; no pre-fill side effect needed here.
    setIsEditing(true);
  };

  const handleCancel = () => {
    setShowRequiredErrors(false);
    if (onCancel) {
      onCancel();
      return;
    }
    setPropertyShortName(pmsConfig?.property_short_name ?? '');
    setDefaultTransactionCategory(pmsConfig?.default_transaction_category ?? '');
    setDefaultPaymentType(pmsConfig?.default_payment_type ?? '');
    setDefaultSplitLedger(pmsConfig?.default_split_ledger ?? '');
    setUnitMappings(
      pmsConfig
        ? buildResmanPersistedUnitMappings(billableUnits)
        : buildResmanInitialUnitMappings(billableUnits),
    );
    setIsEditing(false);
  };

  const handleSave = async () => {
    const formState = {
      propertyShortName,
      defaultTransactionCategory,
      defaultPaymentType,
      defaultSplitLedger,
    };
    const validationError = validateResmanFormState(formState);
    if (validationError) {
      setShowRequiredErrors(true);
      toast.error(validationError, { closeButton: true });
      return;
    }
    setShowRequiredErrors(false);
    setIsSaving(true);
    try {
      const config = buildResmanStructureConfig(formState);
      await onUpdate('RESMAN' as PmsType, config);

      const savePromises: Promise<void>[] = [];
      for (const unit of billableUnits) {
        const newName = unitMappings[unit.id]?.trim() || null;
        const oldName = persistedMappings[unit.id] || null;
        if (newName !== oldName) {
          savePromises.push(
            updateUnitPmsConfig(
              unit.id,
              newName ? 'RESMAN' : null,
              newName ? buildResmanUnitConfig(newName) : null,
            ),
          );
        }
      }
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
      }
      setIsEditing(false);
      onRefresh?.();
      toast.success('PMS settings updated successfully', { closeButton: true });
    } catch {
      toast.error('Failed to save PMS settings', { closeButton: true });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearPms = async () => {
    setIsSaving(true);
    try {
      await onUpdate(null, null);
      await Promise.all(
        billableUnits.filter((u) => u.pms_config).map((u) => updateUnitPmsConfig(u.id, null, null)),
      );
      onRefresh?.();
      setShowClearConfirm(false);
      toast.success('PMS settings cleared', { closeButton: true });
    } catch {
      toast.error('Failed to clear PMS settings', { closeButton: true });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card sx={{ mt: 2, mb: 8 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            ResMan Export Settings
          </Typography>
          {!isEditing && (
            <IconButton
              aria-label="edit"
              onClick={handleStartEdit}
              className={classes.editIcon}
              size="small"
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Configure ResMan integration to export billing data as a CSV that you can import via the
          ResMan charges template.
        </Typography>

        {/* Property Name */}
        <Box sx={{ mb: 3 }}>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            Property Name
            {isEditing && (
              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                *
              </Box>
            )}
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth
              size="small"
              value={propertyShortName}
              onChange={(e) => {
                setPropertyShortName(e.target.value);
                if (showRequiredErrors && e.target.value.trim()) {
                  setShowRequiredErrors(false);
                }
              }}
              placeholder="e.g., 621 Homestead"
              error={showRequiredErrors && !propertyShortName.trim()}
              helperText={
                showRequiredErrors && !propertyShortName.trim()
                  ? 'Property Name is required'
                  : 'The property name used in your ResMan CSV imports'
              }
              disabled={isSaving}
            />
          ) : (
            <Typography className={classes.fieldValue}>
              {pmsConfig?.property_short_name || 'Not set'}
            </Typography>
          )}
        </Box>

        {/* Default Transaction Category */}
        <Box sx={{ mb: 3 }}>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            Default Transaction Category
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth
              size="small"
              value={defaultTransactionCategory}
              onChange={(e) => setDefaultTransactionCategory(e.target.value)}
              placeholder="e.g., Water"
              helperText="Pre-fills the Transaction Category column on every row."
              disabled={isSaving}
            />
          ) : (
            <Typography
              className={
                pmsConfig?.default_transaction_category ? classes.fieldValue : classes.placeholder
              }
            >
              {pmsConfig?.default_transaction_category || 'Not set'}
            </Typography>
          )}
        </Box>

        {/* Default Payment Type */}
        <Box sx={{ mb: 3 }}>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            Default Payment Type
          </Typography>
          {isEditing ? (
            <TextField
              select
              fullWidth
              size="small"
              value={defaultPaymentType}
              onChange={(e) => setDefaultPaymentType(e.target.value)}
              helperText="Pre-fills the Payment Type column on every row."
              disabled={isSaving}
            >
              <MenuItem value="">(leave blank)</MenuItem>
              {RESMAN_PAYMENT_TYPES.map((pt) => (
                <MenuItem key={pt} value={pt}>
                  {pt}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography
              className={pmsConfig?.default_payment_type ? classes.fieldValue : classes.placeholder}
            >
              {pmsConfig?.default_payment_type || '(blank)'}
            </Typography>
          )}
        </Box>

        {/* Default Split Ledger */}
        <Box sx={{ mb: 3 }}>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            Default Split Ledger (Abbreviation)
          </Typography>
          {isEditing ? (
            <TextField
              fullWidth
              size="small"
              value={defaultSplitLedger}
              onChange={(e) => setDefaultSplitLedger(e.target.value)}
              placeholder="Leave blank if unsure"
              helperText="Pre-fills the Split Ledger column. Used for HAP/Non-HAP separation on affordable-housing properties."
              disabled={isSaving}
            />
          ) : (
            <Typography
              className={pmsConfig?.default_split_ledger ? classes.fieldValue : classes.placeholder}
            >
              {pmsConfig?.default_split_ledger || 'Not set'}
            </Typography>
          )}
        </Box>

        {/* Unit Name Mappings */}
        <Box>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            Unit Name Mappings
          </Typography>
          {!isEditing && (
            <Typography className={classes.fieldValue} sx={{ mb: 1 }}>
              {mappedCount > 0
                ? `${mappedCount} of ${billableUnits.length} units mapped`
                : 'No mappings configured'}
            </Typography>
          )}
          {isEditing && (
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>SimpleSub Unit Name</TableCell>
                    <TableCell>ResMan Unit Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billableUnits.map((unit) => (
                    <TableRow key={unit.id}>
                      <TableCell>
                        <Typography variant="body2">{unit.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={unitMappings[unit.id] || ''}
                          onChange={(e) =>
                            setUnitMappings((prev) => ({ ...prev, [unit.id]: e.target.value }))
                          }
                          disabled={isSaving}
                          placeholder={unit.name}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Clear PMS (only when configured and not in edit mode) */}
        {pmsConfig && !isEditing && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={() => setShowClearConfirm(true)}
              disabled={isSaving}
            >
              Clear PMS
            </Button>
          </Box>
        )}

        {/* Cancel / Save (in edit mode) */}
        {isEditing && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mt: 3,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <SWLoadingButtonOutlined onClick={handleCancel} disabled={isSaving}>
              Cancel
            </SWLoadingButtonOutlined>
            <SWLoadingButton onClick={handleSave} loading={isSaving}>
              Save
            </SWLoadingButton>
          </Box>
        )}
      </CardContent>

      <Dialog open={showClearConfirm} onClose={() => !isSaving && setShowClearConfirm(false)}>
        <DialogTitle>Clear PMS Settings?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will remove the ResMan configuration and all unit mappings for this property. You
            can reconfigure at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClearConfirm(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleClearPms} disabled={isSaving}>
            {isSaving ? <CircularProgress size={16} /> : 'Clear PMS'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
